import _ from 'lodash'
import { safeAwait } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'
import useCloneRequestController from './useCloneRequestController'
import useMomoListing from './useMomoListing'
import useShopeeListing from './useShopeeListing'
import useProposalAssetsUpload from './useProposalAssetsUpload'
import useCreateProposal from './useCreateProposal'
import { getError } from '../../../../../lib/reserviceErrorHelpers'
import { getCauseError } from '../helpers'
import { CAUSE_CODE } from '../constants'

/**
 * Pure step runner: (step, inputs) => Promise<{ success, step }>.
 * No store access inside — caller passes urlFormValues, setProposal, setProposalResult, setStepError.
 */
export const createRunStep = (deps) => {
  const {
    signal,
    getIsAborted,
    waitWithAbort,
    triggerUpload,
    momoTrigger,
    shopeeTrigger,
    createProposalTrigger
  } = deps

  return async (step, { urlFormValues, setProposal, setProposalResult, setStepError }) => {
    const failedStep = { success: false, step }
    const nextStep = { success: true, step: step + 1 }
    const currentStep = { success: true, step: -1 }
    const waitInstance = waitWithAbort(1000)

    switch (step) {
      case 1: {
        const platform = _.get(urlFormValues, 'platform')
        const triggerByPlatform = {
          momo: momoTrigger,
          shopee: shopeeTrigger
        }
        const chosenTrigger = triggerByPlatform[platform]
        if (!chosenTrigger) {
          setStepError(getCauseError(CAUSE_CODE.EXTERNAL_CLONE_NO_MATCHING_PLATFORM))
          return failedStep
        }

        const [httpError, responses] = await safeAwait(
          Promise.all([
            chosenTrigger(urlFormValues),
            waitInstance
          ])
        )
        const { proposal, error: proposalError } = _.get(responses, '0', {})
        if (httpError || proposalError) {
          setStepError(
            getError(httpError, proposalError) || getCauseError(CAUSE_CODE.EXTERNAL_CLONE_FETCH_DATA_ERROR)
          )
          return failedStep
        }

        setProposal(proposal)
        return nextStep
      }

      case 2: {
        const [httpError, responses] = await safeAwait(
          Promise.all([
            triggerUpload(),
            waitInstance
          ])
        )
        const { error: uploadError } = _.get(responses, '0', {})
        if (httpError || uploadError) {
          setStepError(
            getError(httpError, uploadError) || getCauseError(CAUSE_CODE.EXTERNAL_CLONE_UPLOAD_ERROR)
          )
          return failedStep
        }

        return nextStep
      }

      case 3: {
        const [httpError, responses] = await safeAwait(
          Promise.all([
            createProposalTrigger(urlFormValues),
            waitInstance
          ])
        )
        const { proposal: proposalResult, error: proposalError } = _.get(responses, '0', {})
        if (httpError || proposalError) {
          setStepError(
            getError(httpError, proposalError) || getCauseError(CAUSE_CODE.EXTERNAL_CLONE_CREATE_PROPOSAL_ERROR)
          )
          return failedStep
        }

        setProposalResult(proposalResult)
        return currentStep
      }

      default: {
        setStepError(null)
        setProposal(null)
        setProposalResult(null)
        const [error] = await safeAwait(waitInstance)
        if (getIsAborted(signal, error)) {
          setStepError(error || getCauseError(CAUSE_CODE.EXTERNAL_CLONE_OPERATION_ERROR))
          return failedStep
        }

        return currentStep
      }
    }
  }
}

/**
 * Returns runStep(step, { urlFormValues, setProposal, setProposalResult, setStepError }).
 * Caller (coordinator) reads urlFormValues from store and passes inputs.
 */
const useHandleSteps = () => {
  const { signal, getIsAborted, waitWithAbort } = useCloneRequestController()
  const { triggerUpload } = useProposalAssetsUpload()
  const { trigger: momoTrigger } = useMomoListing()
  const { trigger: shopeeTrigger } = useShopeeListing()
  const { trigger: createProposalTrigger } = useCreateProposal()

  return createRunStep({
    signal,
    getIsAborted,
    waitWithAbort,
    triggerUpload,
    momoTrigger,
    shopeeTrigger,
    createProposalTrigger
  })
}

export default useHandleSteps
